import * as React from "react";
import { categoryMeta, type CategoryId } from "./tokens";
import { IconChevronUp } from "./icons";
import { IssueList } from "./IssueList";
import type { RowWithIndex } from "./types";
import type { RowReviewStatus } from "./utils";
import type { CategoryExpansion } from "./hooks/usePanelNavigation";

export type CategoryRowProps = {
  category: CategoryId;
  count: number;
  items: RowWithIndex[];
  expansion: CategoryExpansion;
  selectedKey: string | null;
  focusedKey: string | null;
  aiRowStatus: Record<string, RowReviewStatus>;
  onToggle: () => void;
  onExpand: () => void;
  onCollapse: () => void;
  onOpenIssue: (item: RowWithIndex, category: CategoryId) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
};

export const CategoryRow: React.FC<CategoryRowProps> = ({
  category,
  count,
  items,
  expansion,
  selectedKey,
  focusedKey,
  aiRowStatus,
  onToggle,
  onExpand,
  onCollapse,
  onOpenIssue,
  onKeyDown,
}) => {
  const meta = categoryMeta[category];
  const disabled = count === 0;
  const isExpanded = expansion === "expanded";
  const visibleItems = isExpanded ? items : items.slice(0, 1);
  const hiddenCount = Math.max(0, count - 1);

  if (disabled) {
    return (
      <div className="ai-analysis__category ai-analysis__category--disabled">
        <div className="ai-analysis__category-header" aria-disabled>
          <span className="ai-analysis__category-marker" style={{ background: meta.sevVar }} />
          <span className="ai-analysis__category-title">{meta.label}</span>
          <span className="ai-analysis__category-count">0</span>
        </div>
      </div>
    );
  }

  return (
    <div className="ai-analysis__category">
      <button
        type="button"
        className="ai-analysis__category-header"
        onClick={onToggle}
        aria-expanded={isExpanded}
      >
        <span className="ai-analysis__category-marker" style={{ background: meta.sevVar }} />
        <span className="ai-analysis__category-title">{meta.label}</span>
        <span className="ai-analysis__category-count">{count}</span>
        {isExpanded ? (
          <span className="ai-analysis__category-chevron" aria-hidden>
            <IconChevronUp size={12} />
          </span>
        ) : null}
      </button>

      <div className="ai-analysis__category-body">
        <IssueList
          items={visibleItems}
          category={category}
          selectedKey={selectedKey}
          focusedKey={focusedKey}
          aiRowStatus={aiRowStatus}
          onOpenIssue={onOpenIssue}
          onKeyDown={onKeyDown}
        />

        {!isExpanded && hiddenCount > 0 ? (
          <button
            type="button"
            className="ai-analysis__view-more"
            onClick={onExpand}
          >
            View more ({hiddenCount})
          </button>
        ) : null}

        {isExpanded && count > 1 ? (
          <button
            type="button"
            className="ai-analysis__collapse"
            onClick={onCollapse}
          >
            <IconChevronUp size={12} />
            Collapse
          </button>
        ) : null}
      </div>
    </div>
  );
};
